import { createMutation, createQuery, createSuspenseQuery, makeQueryKey } from "@steto-care/steto-query";

import { ManagePatientRepository } from "@/io/repository/ManagePatientRepository.ts";
import { ObservationsRepository } from "@/io/repository/ObservationsRepository.ts";
import { UploadFileRepository } from "@/io/repository/UploadFileRepository.ts";
import { SearchForm } from "@/models/AnterioritySearchModel.ts";
import { CarePlanInterruptionReason } from "@/models/CarePlanInterruptionModel.ts";
import { CreateContact } from "@/models/ContactModel.ts";
import { CreatePatientModel } from "@/models/CreatePatientModel.ts";
import { EditTherapeuticObjectivesData } from "@/models/EditTherapeuticObjectivesModel.ts";
import { ExistingPatientQuery } from "@/models/ExistingPatientModel.ts";
import { UploadFileModel, type FileModel } from "@/models/FileModel.ts";
import { ValidateOrRejectIdentity } from "@/models/identity/FrenchIdentity.ts";
import { CreateNurseInclusionSuggestionRequest } from "@/models/NurseInclusionPatientModel.ts";
import type { Patient } from "@/models/PatientModel.ts";
import { RenewType } from "@/models/PatientRenewalInfoModel.ts";
import { RefuseReinclusionReason } from "@/models/ReinclusionAgentSuggestionModel.ts";
import { RenewCarePlanData } from "@/models/RenewCarePlanModel.ts";

export class PractitionerQueries {
  constructor(
    private readonly managePatient: ManagePatientRepository = new ManagePatientRepository(),
    private readonly observation: ObservationsRepository = new ObservationsRepository(),
    private readonly uploadFile: UploadFileRepository = new UploadFileRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useNursePatientList = createSuspenseQuery(
    "nurse-patients",
    ({}) => [],
    async ({}) => this.managePatient.getNursePatientList(),
    {
      staleTime: 30_000,
    },
  );

  useDoctorPatientList = createSuspenseQuery(
    "doctor-patients",
    ({}) => [],
    async () => this.managePatient.getDoctorPatientList(),
    {
      staleTime: 30_000,
    },
  );

  usePatient = createSuspenseQuery<string, Patient>(
    "observed-patient",
    patientId => [patientId],
    async (patientId: string, { client }) => {
      const result = await this.managePatient.getPatient(patientId);
      this.useDoctorPatientList.invalidate(client, patientId);
      this.useNursePatientList.invalidate(client, patientId);
      return result;
    },
    {
      staleTime: 30_000,
    },
  );

  useIdentitiesToValidate = createQuery(
    "identities-to-validate",
    ({}) => [],
    async () => this.managePatient.getIdentitiesToValidate(),
  );

  useNurseNotice = createSuspenseQuery(
    "nurse-notice",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.observation.getNurseNotice(patientId),
  );

  useCareTeam = createQuery(
    "care-team",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getCareTeam(patientId),
  );

  useCurrentCarePlanInfo = createSuspenseQuery(
    "current-care-plan-info",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getCurrentCarePlanInfo(patientId),
  );

  useCarePlanHistory = createSuspenseQuery(
    "care-plan-list",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.carePlansHistory(patientId),
  );

  useCarePlanDirectives = createSuspenseQuery(
    "care-plan-directives",
    data => makeQueryKey(data.renewalType, data.patientId),
    async (data: { renewalType: RenewType; patientId: string }) =>
      this.managePatient.getCarePlanDirectives(data.renewalType, data.patientId),
    {
      staleTime: 0,
    },
  );

  usePatientCreationDirectives = createSuspenseQuery(
    "patient-creation-directives",
    data => makeQueryKey(data.preinclusionId),
    async (data: { preinclusionId: string | undefined }) =>
      this.managePatient.getPatientCreationDirectives(data.preinclusionId),
  );

  useNursePreInclusionDirectives = createSuspenseQuery(
    "nurse-pre-inclusion-directives",
    () => makeQueryKey(),
    async () => this.managePatient.getNurseInclusionDirectives(),
  );

  useListContacts = createQuery(
    "patient-contacts",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getListContacts(patientId),
  );

  useDiabetesDisplayMode = createSuspenseQuery(
    "glycemia-display-mode",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getDiabetesDisplayMode(patientId),
  );

  useHSPList = createSuspenseQuery(
    "practitioner-hsp",
    ({}) => [],
    async () => this.managePatient.listHSP(),
  );

  usePatientEvents = createSuspenseQuery(
    "patient-events",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getPatientEvents(patientId),
  );

  usePatientRenewInfo = createSuspenseQuery(
    "patient-renew-info",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getPatientRenewalInfo(patientId),
  );

  useInclusionAgentPatients = createSuspenseQuery(
    "inclusion-agent-patients",
    ({}) => [],
    () => this.managePatient.getInclusionAgentPatients(),
  );

  useGroupedInclusionPatients = createSuspenseQuery(
    "grouped-inclusion-patients",
    ({}) => [],
    () => this.managePatient.getGroupedInclusionPatients(),
  );

  // Nurse Inclusion - Doctor view (patients suggested to doctor)
  useNurseInclusionPatients = createSuspenseQuery(
    "nurse_inclusion_patients",
    ({}) => [],
    () => this.managePatient.getNurseInclusionPatients(),
  );

  // Nurse Inclusion - Nurse view (suggestions created by nurse)
  useNurseInclusionSuggestions = createSuspenseQuery(
    "nurse_inclusion_suggestions",
    ({}) => [],
    () => this.managePatient.getNurseInclusionSuggestions(),
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  useCreatePatient = createMutation(
    "create-patient",
    async (data: CreatePatientModel) => this.managePatient.createPatient(data),
    {
      onSuccess: async (_, variables, { client }) => {
        this.useDoctorPatientList.invalidate(client, {});
        this.useNursePatientList.invalidate(client, {});

        if (variables.grouped_inclusion_id) {
          this.useGroupedInclusionPatients.invalidate(client, {});
        }
        if (variables.inclusion_agent_id) {
          this.useInclusionAgentPatients.invalidate(client, {});
        }
        if (variables.nurse_inclusion_id) {
          this.useNurseInclusionPatients.invalidate(client, {});
        }
      },
    },
  );

  useRenewCarePlan = createMutation(
    "renew-care-plan",
    async ({ patientId, data }: { patientId: string; data: RenewCarePlanData }) =>
      this.managePatient.renewCarePlan(patientId, data),
    {
      onSuccess: async (_, { patientId }, { client }) => {
        this.useCurrentCarePlanInfo.clear(client, patientId);
        this.useCarePlanHistory.invalidate(client, patientId);
        this.useDoctorPatientList.invalidate(client, {});
        this.useNursePatientList.invalidate(client, {});
        this.usePatient.invalidate(client, patientId);
        this.useShouldNudgeRenewal.invalidate(client, patientId);
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.useReinclusionStateForDoctor.invalidate(client, patientId);
        this.usePatientRenewInfo.invalidate(client, patientId);
        this.usePatientEvents.invalidate(client, patientId);
        setTimeout(() => {
          this.useCarePlanDirectives.clear(client, { patientId });
        }, 1000);
      },
    },
  );

  useInterruptCarePlan = createMutation(
    "interrupt-care-plan",
    async ({ patientId, reason }: { patientId: string; reason: CarePlanInterruptionReason }) =>
      this.managePatient.interruptCarePlan(patientId, { reason }),
    {
      onSuccess: async (_, { patientId }, { client }) => {
        this.useCurrentCarePlanInfo.clear(client, patientId);
        this.useCarePlanHistory.invalidate(client, patientId);
        this.useDoctorPatientList.invalidate(client, {});
        this.useNursePatientList.invalidate(client, {});
        this.usePatient.invalidate(client, patientId);
        this.useShouldNudgeRenewal.invalidate(client, patientId);
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.useReinclusionStateForDoctor.invalidate(client, patientId);
        this.usePatientRenewInfo.invalidate(client, patientId);
        this.usePatientEvents.invalidate(client, patientId);
      },
    },
  );

  useEditTherapeuticObjectives = createMutation(
    "edit-therapeutic-objectives",
    async ({
      patientId,
      carePlanId,
      data,
    }: {
      patientId: string;
      carePlanId: string;
      data: EditTherapeuticObjectivesData;
    }) => this.managePatient.editTherapeuticObjectives(patientId, carePlanId, data),
    {
      onSuccess: async (_, variables, { client }) => {
        this.useCurrentCarePlanInfo.invalidate(client, variables.patientId);
        this.useCarePlanHistory.invalidate(client, variables.patientId);
      },
    },
  );

  useEditTreatmentPrescription = createMutation(
    "edit-treatment-prescription",
    async ({ patientId, carePlanId, data }: { patientId: string; carePlanId: string; data: FileModel }) => {
      return this.managePatient.editTreatmentPrescription(patientId, carePlanId, data);
    },
    {
      onSuccess: async (_, variables, { client }) => {
        this.useCurrentCarePlanInfo.invalidate(client, variables.patientId);
      },
    },
  );

  useDeleteTreatmentPrescription = createMutation(
    "delete-treatment-prescription",
    async ({ patientId, carePlanId }: { patientId: string; carePlanId: string }) =>
      this.managePatient.deleteTreatmentPrescription(patientId, carePlanId),
    {
      onSuccess: async (_, variables, { client }) => {
        this.useCurrentCarePlanInfo.invalidate(client, variables.patientId);
      },
    },
  );

  useEditReferralLetter = createMutation(
    "edit-referral-letter",
    async ({ patientId, carePlanId, data }: { patientId: string; carePlanId: string; data: FileModel }) =>
      this.managePatient.editReferralLetter(patientId, carePlanId, data),
    {
      onSuccess: async (_, variables, { client }) => {
        this.useCurrentCarePlanInfo.invalidate(client, variables.patientId);
      },
    },
  );

  useDeleteReferralLetter = createMutation(
    "delete-referral-letter",
    async ({ patientId, carePlanId }: { patientId: string; carePlanId: string }) =>
      this.managePatient.deleteReferralLetter(patientId, carePlanId),
    {
      onSuccess: async (_, variables, { client }) => {
        this.useCurrentCarePlanInfo.invalidate(client, variables.patientId);
      },
    },
  );

  useValidateOrRejectIdentity = createMutation(
    "validate-or-reject-identity",
    async ({ patientId, action }: { patientId: string; action: ValidateOrRejectIdentity }) =>
      this.managePatient.validateOrRejectIdentity(patientId, action),
    {
      onSuccess: async (_, { patientId }, { client }) => {
        this.useIdentitiesToValidate.invalidate(client, {});
        this.usePatient.invalidate(client, patientId);
      },
    },
  );

  useCreateContact = createMutation(
    "create-contact",
    async ({ patientId, data }: { patientId: string; data: CreateContact }) =>
      this.managePatient.createPatientContacts(patientId, data),
    {
      onSuccess: (_, { patientId }, { client }) => {
        this.useListContacts.invalidate(client, patientId);
      },
    },
  );

  useUpdateContact = createMutation(
    "update-contact",
    async ({ patientId, contactId, data }: { patientId: string; contactId: string; data: Partial<CreateContact> }) =>
      this.managePatient.updatePatientContacts(patientId, contactId, data),
    {
      onSuccess: (_, { patientId }, { client }) => {
        this.useListContacts.invalidate(client, patientId);
      },
    },
  );

  useDeleteContact = createMutation(
    "delete-contact",
    async ({ patientId, contactId }: { patientId: string; contactId: string }) =>
      this.managePatient.deletePatientContacts(patientId, contactId),
    {
      onSuccess: (_, { patientId }, { client }) => {
        this.useListContacts.invalidate(client, patientId);
      },
    },
  );

  useUploadFile = createMutation("upload-file", async (data: UploadFileModel) => this.uploadFile.uploadFile(data));

  useNurseCancelReinclusion = createMutation(
    "cancel-reinclusion",
    async (patientId: string) => this.managePatient.cancelReinclusion(patientId),
    {
      onSuccess: (_, patientId, { client }) => {
        this.useDoctorPatientList.invalidate(client, {});
        this.useNursePatientList.invalidate(client, {});
        this.usePatient.invalidate(client, patientId);
        this.useCurrentCarePlanInfo.invalidate(client, patientId);
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.useReinclusionStateForDoctor.invalidate(client, patientId);
        this.usePatientEvents.invalidate(client, patientId);
      },
    },
  );

  useDoctorRefuseReinclusion = createMutation(
    "doctor-refuse-reinclusion",
    async (patientId: string) => this.managePatient.refuseReinclusion(patientId),
    {
      onSuccess: (_, patientId, { client }) => {
        this.useDoctorPatientList.invalidate(client, {});
        this.useNursePatientList.invalidate(client, {});
        this.usePatient.invalidate(client, patientId);
        this.useCurrentCarePlanInfo.invalidate(client, patientId);
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.useReinclusionStateForDoctor.invalidate(client, patientId);
        this.usePatientEvents.invalidate(client, patientId);
      },
    },
  );

  useRequestReinclusion = createMutation(
    "request-reinclusion",
    async ({ patientId, nurseMessage }: { patientId: string; nurseMessage: string | undefined }) =>
      this.managePatient.requestReinclusion(patientId, nurseMessage),
    {
      onSuccess: async (_, { patientId }, { client }) => {
        this.useDoctorPatientList.invalidate(client, {});
        this.useNursePatientList.invalidate(client, {});
        this.usePatient.invalidate(client, patientId);
        this.useCurrentCarePlanInfo.invalidate(client, patientId);
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.useReinclusionStateForDoctor.invalidate(client, patientId);
        this.usePatientEvents.invalidate(client, patientId);
      },
    },
  );

  useReinclusionStateForNurse = createSuspenseQuery(
    "reinclusion-state-nurse",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getReinclusionStateForNurse(patientId),
  );

  useReinclusionStateForDoctor = createSuspenseQuery(
    "reinclusion-state-doctor",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getReinclusionStateForDoctor(patientId),
  );

  useAcceptReinclusionAgentSuggestion = createMutation(
    "accept-reinclusion-agent-suggestion",
    async ({ patientId, nurseMessage }: { patientId: string; nurseMessage: string | undefined }) =>
      this.managePatient.acceptReinclusionAgentSuggestion(patientId, nurseMessage),
    {
      onSuccess: async (_, { patientId }, { client }) => {
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.useReinclusionStateForDoctor.invalidate(client, patientId);
        this.useNursePatientList.invalidate(client, {});
        this.useDoctorPatientList.invalidate(client, {});
        this.usePatient.invalidate(client, patientId);
        this.useCurrentCarePlanInfo.invalidate(client, patientId);
        this.usePatientEvents.invalidate(client, patientId);
      },
    },
  );

  useRefuseReinclusionAgentSuggestion = createMutation(
    "refuse-reinclusion-agent-suggestion",
    async ({
      patientId,
      reason,
      reasonFreeText,
    }: {
      patientId: string;
      reason: RefuseReinclusionReason;
      reasonFreeText: string | undefined;
    }) => this.managePatient.refuseReinclusionAgentSuggestion(patientId, reason, reasonFreeText),
    {
      onSuccess: async (_, { patientId }, { client }) => {
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.useNursePatientList.invalidate(client, {});
      },
    },
  );

  useShouldNudgeRenewal = createSuspenseQuery(
    "should-nudge-renewal",
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getRenewalNudge(patientId),
  );

  useRefuseToRenew = createMutation(
    "refuse-to-renew",
    async (patientId: string) => this.managePatient.refuseRenewal(patientId),
    {
      onSuccess: (_, patientId, { client }) => {
        this.useShouldNudgeRenewal.invalidate(client, patientId);
        this.useReinclusionStateForDoctor.invalidate(client, patientId);
        this.useReinclusionStateForNurse.invalidate(client, patientId);
        this.usePatientEvents.invalidate(client, patientId);
      },
    },
  );

  useAcceptInclusionAgentSuggestion = createMutation(
    "accept-inclusion-agent-suggestion",
    async (data: { inclusionAgentId: string; phone: string }) =>
      this.managePatient.acceptInclusionAgentSuggestion(data.inclusionAgentId, data.phone),
    {
      onSuccess: (_, __, { client }) => {
        this.useInclusionAgentPatients.invalidate(client, {});
      },
    },
  );

  useRejectInclusionAgentSuggestion = createMutation(
    "reject-inclusion-agent-suggestion",
    async (inclusionAgentId: string) => this.managePatient.rejectInclusionAgentSuggestion(inclusionAgentId),
    {
      onSuccess: (_, __, { client }) => {
        this.useInclusionAgentPatients.invalidate(client, {});
      },
    },
  );

  useAnterioritySearch = createMutation("anteriority-search", async (searchQuery: SearchForm) =>
    this.managePatient.anterioritySearchPatient(searchQuery),
  );

  useFindExistingPatient = createMutation("find-existing-patient", async (query: ExistingPatientQuery) =>
    this.managePatient.findExistingPatient(query),
  );

  // Nurse Inclusion - Doctor mutations
  useRejectNurseInclusionPatient = createMutation(
    "reject-nurse-inclusion-patient",
    async (nurseInclusionPatientId: string) => this.managePatient.rejectNurseInclusionPatient(nurseInclusionPatientId),
    {
      onSuccess: (_, __, { client }) => {
        this.useNurseInclusionPatients.invalidate(client, {});
      },
    },
  );

  // Nurse Inclusion - Nurse mutations
  useCreateNurseInclusionSuggestion = createMutation(
    "create-nurse-inclusion-suggestion",
    async (data: CreateNurseInclusionSuggestionRequest) => this.managePatient.createNurseInclusionSuggestion(data),
    {
      onSuccess: (_, __, { client }) => {
        this.useNurseInclusionSuggestions.invalidate(client, {});
      },
    },
  );

  useCancelNurseInclusionSuggestion = createMutation(
    "cancel-nurse-inclusion-suggestion",
    async (nurseInclusionPatientId: string) =>
      this.managePatient.cancelNurseInclusionSuggestion(nurseInclusionPatientId),
    {
      onSuccess: (_, __, { client }) => {
        this.useNurseInclusionSuggestions.invalidate(client, {});
      },
    },
  );
}
